import { StackActions } from "@react-navigation/native";
import React, { useCallback, useEffect } from "react";
import {
  BackHandler,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import Text from "@components/CustomText";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@assets/svg/CloseIcon";
import CheckMultiLayer from "@assets/svg/CheckMultiLayer";
import ThankYouPageModalLeft from "@assets/svg/ThankYouPageModalLeft";
import ThankYouPageModalRight from "@assets/svg/ThankYouPageModalRight";
import FetchCMSEntity from "@components/CMS/FetchCMSEntity";
import analytics from "@services/analytics";
import { actions as customerActions } from "@reducers/customerReducer";
import {
  ORDER_FLOW_EVENTS,
  THANK_YOU_ORDER_DETAILS,
} from "@constants/analyticsEventsCT";
import CartBottomStrip from "../Home/CartBottomStrip";
import OrderDetailsSection from "./OrderDetailsSection";

import t from "@constants/localization";
import useCMSEvents from "@hooks/useCMSEvents";

const PATH = "screens/PlaceOrderSuccess/PlaceOrderSuccess";

const PlaceOrderSuccess = ({ route, navigation }) => {
  const { params } = route;
  const dispatch = useDispatch();

  const {
    splitOrders = [],
    billingDetails = [],
    commission = "",
    orderId = "",
    inventoryOrder = false,
  } = params;

  const trackEvent = useCMSEvents("placeorder");

  const { selectedContact, cartDetails, cartItems, cart, orders } = useSelector(
    (state) => ({
      selectedContact: state.customer.selectedContact,
      cartDetails: state.cart.cartDetails,
      cartItems: state.cart.cartItems,
      cart: state.cart,
      orders: state.orders,
    }),
  );

  const backhandler = useCallback(() => {
    navigateToHome();
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToOrdersList = () => {
    if (inventoryOrder) {
      analytics.trackEvent(THANK_YOU_ORDER_DETAILS, {
        order_id: orderId || "",
      });
      navigation.navigate("OtherOrdersList", { fromInventoryOrder: true });
      return true;
    }
    analytics.trackEvent(THANK_YOU_ORDER_DETAILS, {
      order_id: orderId || "",
    });
    // dispatch(cartActions.getAllCartItems(selectedContact.user_id));
    // dispatch(cartActions.setCommission(0));
    navigation.navigate("OrdersList", { fromShop: false });
    return true;
  };

  useEffect(() => {
    const subscription = BackHandler.addEventListener("hardwareBackPress", backhandler);
    return () => {
      subscription.remove();
    };
  }, [backhandler]);

  const navigateToHome = () => {
    navigation.dispatch(
      StackActions.replace("HomeStack", {
        showVacationModal: true,
      }),
    );
    // dispatch(cartActions.setCommission(0));
  };

  const appSettings = useSelector((s) => s.configReducer.appSettings);
  const { cmsPoweredEntities = {} } = appSettings || {};
  const CMS_KEY = "THANK_YOU_PAGE";

  return (
    <>
      <ScrollView style={styles.container}>
        <View style={styles.wrapper}>
          <CloseButton navigateToHome={navigateToHome} />
          <Header
            commission={commission}
            // orderId={orderId}
            splitOrders={splitOrders}
          />
        </View>
        <OrderDetailsSection
          splitOrders={splitOrders}
          billingDetails={billingDetails}
          goToOrdersList={goToOrdersList}
        />
        {cmsPoweredEntities &&
        typeof cmsPoweredEntities === "object" &&
        CMS_KEY in cmsPoweredEntities ? (
          <FetchCMSEntity
            trackEvent={trackEvent}
            id={cmsPoweredEntities[CMS_KEY]}
            type="PAGE"
          />
        ) : null}
      </ScrollView>
      {selectedContact && (
        <CartBottomStrip
          bottom={{ bottom: 0 }}
          orderClick={() => {
            navigation.push("ConfirmOrder");
            analytics.trackEvent(ORDER_FLOW_EVENTS.CHECKOUT_STARTED, {
              screen: "Thank You Page",
              cart_value: cartDetails.total,
              cart_quantity: Array.isArray(cartItems)
                ? cartItems.reduce((acc, curr) => (acc += curr.quantity), 0)
                : "",
            });
          }}
          onCrossClick={() => {
            dispatch(customerActions.setSelectedContact(false));
            analytics.trackEvent(ORDER_FLOW_EVENTS.CX_CANCELLED);
          }}
          total={cartDetails.total}
          item={cartItems.length}
          name={selectedContact.user_name}
        />
      )}
    </>
  );
};

const BottomGraphic = () => (
  <View
    style={{
      justifyContent: "space-between",
      flexDirection: "row",
      alignItems: "flex-end",
      paddingBottom: 4,
    }}
  >
    <ThankYouPageModalLeft />
    <ThankYouPageModalRight />
  </View>
);

const CloseButton = ({ navigateToHome }) => (
  <View style={[styles.beLikeRow, { justifyContent: "space-between" }]}>
    <TouchableOpacity
      onPress={() => {
        navigateToHome();
      }}
      style={{
        position: "absolute",
        top: 15,
        left: 10,
        paddingTop: 10,
        zIndex: 9,
      }}
    >
      <CloseIcon fill="rgba(0, 0, 0, 0.8)" height={25} width={25} />
    </TouchableOpacity>
  </View>
);

const Header = ({ commission, splitOrders = [] }) => (
  <View style={styles.graphicContainer}>
    <CheckMultiLayer />
    <View
      style={{
        justifyContent: "center",
        alignItems: "center",
        marginTop: 15,
      }}
    >
      <Text
        style={{
          color: "black",
          fontFamily: "NotoSansDevanagari-Regular",
          fontSize: 14,
          lineHeight: 20,
          marginBottom: 4,
        }}
      >
        {t(`${PATH}/orderPlacedMsg`)}
      </Text>
    </View>
    {(typeof commission === "number" && commission > 0) ||
    (typeof commission === "string" && commission.length) ? (
      <>
        <Text
          style={{
            color: "black",
            fontSize: 20,
            lineHeight: 30,
            fontFamily: "NotoSansDevanagari-Bold",
          }}
        >
          ₹{commission.toFixed(2)} {t(`${PATH}/commissionEarned`)}
        </Text>
      </>
    ) : (
      <></>
    )}
  </View>
);

const styles = StyleSheet.create({
  container: {
    backgroundColor: "white",
    flex: 1,
  },
  beLikeRow: {
    flexDirection: "row",
  },
  wrapper: {
    backgroundColor: "rgba(0, 186, 110, 0.1)",
    paddingHorizontal: 15,
    marginBottom: 15,
  },
  graphicContainer: {
    position: "relative",
    alignItems: "center",
    width: "100%",
    overflow: "hidden",
    paddingTop: 40,
    paddingBottom: 16,
  },
});

export { PlaceOrderSuccess };
